import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Radio } from 'antd';
import { notification, Flex } from 'ui';
import { UploadButton } from './upload-xml-button';

const ErrorTitle = styled.p`
  font-weight: bold;
  font-size: 15px;
`;

const StyledRadio = styled(Radio)`
  font-size: 15px;
  margin-left: 15px;
  margin-top: 5px;
  width: 100%;
  .ant-radio-inner::after {
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    border-radius: 50%;
  }

  .ant-radio-inner {
    height: 20px;
    width: 20px;
    position: unset;
  }
`;

const StyledLink = styled.a`
  text-decoration: underline;
  cursor: pointer;
`;

const StyledIcon = styled.i`
  font-size: 12px;
  margin-right: 5px;
`;

const Buttons = styled(Flex)`
  > :not(:last-child) {
    margin-right: 5px;
  }
  justify-content: flex-end;
  margin-top: 15px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  > :first-child {
    margin-top: 15px;
  }
  margin-bottom: 15px;
`;

const StyledText = styled.p`
  margin: 15px 0;
`;


const Link = styled.p`
  color: #0098cc;
  text-decoration: underline;
  font-size: 16px;
  line-height: 32px;
  margin-right: auto !important;
  cursor: pointer;
`;

const StyledInfoReference = styled.p`
  margin-top: 10px;
  font-size: 12px;
`;

const StyledList = styled.ul`
  margin-bottom: 10px;
  padding-left: 20px;
`;

const StyledListItem = styled.li`
  margin-top: 5px;
  list-style-type: circle;
`;

const StyledDiv = styled.div`
  margin-top: 5px;
`;

const DuplicationError = ({
  title,
  errors,
  reference,
  intl,
  createXMLRequest,
  selectedEquipment,
  actions,
  setSelectedEquipment,
  numberFound,
  isMulti = false,
  hasNext = false,
  hasPrevious = false,
  goToNextProblem,
  goToPrevProblem,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const renderErrorContent = () =>
    <>
      <StyledText>{intl.formatMessage({ id: 'fileUpload.SELECT_INSTRUCTION' })}</StyledText>
      <StyledRadioGroup onChange={setSelectedEquipment} value={selectedEquipment}>
        {actions?.map((a, i) =>
          <StyledRadio
            key={i}
            value={i}
          >
            <span>{isMulti ? a.name : a.info.EquipmentName}</span>
            <p>
              <StyledLink
                href={`${window.location.origin}/equipment/${isMulti ? a.id : a.info.EquipmentId}`}
                target='_blank'
              >
                <StyledIcon className='fas fa-external-link-alt' />
                {intl.formatMessage({ id: 'fileUpload.SHOW_DETAILS' })}
              </StyledLink>
            </p>
          </StyledRadio>,
        )}
      </StyledRadioGroup>
    </>;

  return (
    <>
      {title && <ErrorTitle>{title}</ErrorTitle>}
      {errors.length > 1 ?
        <StyledList>
          {errors?.map(e =>
            <StyledListItem key={e}>
              {e}
              {renderErrorContent()}
            </StyledListItem>,
          )}
        </StyledList> :
        errors?.map(e =>
          <StyledDiv key={e}>
            {e}
            {renderErrorContent()}
          </StyledDiv>,
        )
      }
      {isMulti ?
        <p>{`${hasNext ?
          intl.formatMessage({ id: 'fileUpload.NEXT_PROBLEM' }) :
          intl.formatMessage({ id: 'fileUpload.NEXT_CREATE' }, { numberFound })
          } ${intl.formatMessage({ id: 'fileUpload.ALT' })}`}</p> :
        null
      }
      <StyledInfoReference>{reference}</StyledInfoReference>
      <Buttons>
        {hasPrevious ?
          <Link onClick={goToPrevProblem}>
            {`< ${intl.formatMessage({ id: 'fileUpload.BACK' })}`}
          </Link> :
          null
        }
        <UploadButton
          onClick={() => notification.destroy('errorNotification')}
          disabled={isLoading}
        >
          {intl.formatMessage({ id: 'fileUpload.CANCEL_UPLOAD' })}
        </UploadButton>
        {hasNext ?
          <UploadButton
            primary={1}
            onClick={goToNextProblem}
            disabled={selectedEquipment < 0}
          >
            {`${intl.formatMessage({ id: 'fileUpload.NEXT' })} >`}
          </UploadButton> :
          <UploadButton
            primary={1}
            onClick={
              () => {
                setIsLoading(true);
                createXMLRequest(
                  actions[selectedEquipment],
                );
              }}
            disabled={isLoading || selectedEquipment < 0}
            loading={isLoading}
          >
            {intl.formatMessage({ id: 'fileUpload.CONTINUE_UPLOAD' })}
          </UploadButton>
        }
      </Buttons>
    </>
  );
};

DuplicationError.propTypes = {
  title: PropTypes.string,
  errors: PropTypes.array,
  reference: PropTypes.string,
  intl: PropTypes.object,
  createXMLRequest: PropTypes.func,
  selectedEquipment: PropTypes.number,
  actions: PropTypes.array,
  setSelectedEquipment: PropTypes.func,
  isMulti: PropTypes.bool,
  numberFound: PropTypes.number,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  goToNextProblem: PropTypes.func,
  goToPrevProblem: PropTypes.func,
};

export default DuplicationError;
